import { useEffect, useState } from 'react';

import { isIncomingCallPermissionGrantedMessage } from '@/utils/incoming-call-notification';

// Must match STORAGE_KEY in public/enable-notifications.html.
// `Notification.permission` read from inside the HitPhone iframe used to be
// always reported as 'denied' by Chrome regardless of the real per-origin
// grant, but that masking only applies when the embedder hasn't delegated
// the 'notifications' permission policy to the iframe. OMNIHIT's embed now
// sets allow="notifications", so the real value is readable there — checked
// first as the authoritative signal. Embeds that don't delegate it (e.g. a
// Salesforce Lightning/Visualforce frame without that `allow`) still fall
// back to this shared, same-origin localStorage flag as a best effort — it
// may not be visible here if the browser partitions storage between this
// third-party iframe and the tab (its own top-level site) that set it, so a
// direct postMessage from that tab (via window.opener) is the
// partitioning-proof signal for those; localStorage only helps it persist
// across reloads when it isn't partitioned.
const STORAGE_KEY = 'hit-incoming-call-notifications-enabled';

const isNotificationPermissionGranted = (): boolean =>
  typeof Notification !== 'undefined' && Notification.permission === 'granted';

// Persist the grant in the iframe's own storage partition so a reload does
// not bring back the permission banner after the permission tab has closed.
export const persistEnabled = () => {
  try {
    localStorage.setItem(STORAGE_KEY, '1');
  } catch {
    // Storage unavailable: the grant still applies to this session.
  }
};

export const readEnabled = (): boolean => {
  if (isNotificationPermissionGranted()) return true;
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

export const useIncomingCallNotificationsEnabled = (): boolean => {
  const [enabled, setEnabled] = useState(readEnabled);

  useEffect(() => {
    // Once granted, it never needs to go back to false within a session — so
    // stop listening as soon as we know, instead of risking a stale re-check
    // (e.g. localStorage partitioned/inaccessible here) flipping it back off.
    if (enabled) return;

    const checkStorage = () => {
      if (readEnabled()) setEnabled(true);
    };
    const onMessage = (event: MessageEvent) => {
      if (
        event.origin === window.location.origin &&
        isIncomingCallPermissionGrantedMessage(event.data)
      ) {
        persistEnabled();
        setEnabled(true);
      }
    };

    window.addEventListener('focus', checkStorage);
    window.addEventListener('storage', checkStorage);
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('focus', checkStorage);
      window.removeEventListener('storage', checkStorage);
      window.removeEventListener('message', onMessage);
    };
  }, [enabled]);

  return enabled;
};
